import React, { useState, useEffect } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { Scale, Save, FileText, MessageSquare, Star, Eye, PenTool, Plus, Sparkles, Loader2, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { CITIES, CATEGORIES } from '../mock/mock';
import { firmsApi, aiApi } from '../api/client';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';
import { SubscriptionTab } from '../components/dashboard/SubscriptionTab';
import { SeoScoreCard } from '../components/dashboard/SeoScoreCard';
import { InboxTab } from '../components/dashboard/InboxTab';
import { CrmTab } from '../components/dashboard/CrmTab';
import { AgendaTab } from '../components/dashboard/AgendaTab';

export default function Dashboard() {
  const { user, firm, setFirm, loading } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'perfil';
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // article + post dialogs
  const [articleOpen, setArticleOpen] = useState(false);
  const [article, setArticle] = useState({ title: '', excerpt: '', content: '', cover: '', readTime: '5 min', scheduledAt: '', area: '' });
  const [aiTopic, setAiTopic] = useState('');
  const [aiArea, setAiArea] = useState('trabalhista');
  const [aiTone, setAiTone] = useState('profissional');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [postContent, setPostContent] = useState('');

  useEffect(() => {
    if (firm) setForm({
      name: firm.name, leadLawyer: firm.leadLawyer, oab: firm.oab, city: firm.city,
      whatsapp: firm.whatsapp, phone: firm.phone || '', email: firm.email, website: firm.website || '',
      address: firm.address, photo: firm.photo, cover: firm.cover, bio: firm.bio,
      description: firm.description, categories: firm.categories,
      whoWeServe: (firm.whoWeServe || []).join('\n'),
      differentials: (firm.differentials || []).join('\n'),
      awards: (firm.awards || []).join('\n'),
      social: firm.social || {},
    });
  }, [firm]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[#52676e]">Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!firm || !form) return <div className="min-h-[60vh] flex items-center justify-center text-[#52676e]">Nenhum escritório vinculado.</div>;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm(f => ({ ...f, social: { ...f.social, [k]: v } }));

  const toggleCat = (slug) => {
    setForm(f => ({ ...f, categories: f.categories.includes(slug) ? f.categories.filter(c => c !== slug) : [...f.categories, slug] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        whoWeServe: form.whoWeServe.split('\n').map(s => s.trim()).filter(Boolean),
        differentials: form.differentials.split('\n').map(s => s.trim()).filter(Boolean),
        awards: form.awards.split('\n').map(s => s.trim()).filter(Boolean),
      };
      const updated = await firmsApi.update(firm.id, payload);
      setFirm(updated);
      toast({ title: 'Salvo!', description: 'Perfil atualizado com sucesso.' });
    } catch (err) {
      toast({ title: 'Erro', description: err.response?.data?.detail || 'Falha ao salvar.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const publishArticle = async () => {
    if (!article.title || !article.excerpt) return;
    try {
      const payload = {
        title: article.title,
        excerpt: article.excerpt,
        content: article.content,
        cover: article.cover,
        readTime: article.readTime,
        scheduledAt: article.scheduledAt ? new Date(article.scheduledAt).toISOString() : null,
      };
      const a = await firmsApi.addArticle(firm.id, payload);
      setFirm({ ...firm, articles: [a, ...(firm.articles || [])] });
      setArticle({ title: '', excerpt: '', content: '', cover: '', readTime: '5 min', scheduledAt: '', area: '' });
      setArticleOpen(false);
      toast({ title: payload.scheduledAt ? 'Artigo agendado!' : 'Artigo publicado!', description: payload.scheduledAt ? `Será publicado em ${new Date(payload.scheduledAt).toLocaleString('pt-BR')}.` : 'Já está disponível no blog.' });
    } catch (err) { toast({ title: 'Erro', description: 'Falha ao publicar.', variant: 'destructive' }); }
  };

  const generateWithAI = async () => {
    if (aiTopic.length < 5) {
      toast({ title: 'Descreva o tema', description: 'O tema precisa ter pelo menos 5 caracteres.', variant: 'destructive' });
      return;
    }
    setAiGenerating(true);
    try {
      const result = await aiApi.generateArticle(aiTopic, aiArea, aiTone);
      setArticle({
        title: result.title || aiTopic,
        excerpt: result.excerpt || '',
        content: result.content || '',
        cover: firm.cover || '',
        readTime: result.readTime || '5 min',
        scheduledAt: article.scheduledAt,
        area: aiArea,
      });
      toast({ title: 'Rascunho gerado!', description: 'Revise e ajuste antes de publicar.' });
    } catch (err) {
      toast({ title: 'Erro na IA', description: err.response?.data?.detail || 'Tente novamente.', variant: 'destructive' });
    } finally { setAiGenerating(false); }
  };

  const publishPost = async () => {
    if (postContent.length < 3) return;
    try {
      const p = await firmsApi.addPost(firm.id, { content: postContent });
      setFirm({ ...firm, posts: [p, ...(firm.posts || [])] });
      setPostContent(''); setPostOpen(false);
      toast({ title: 'Post publicado!' });
    } catch (err) { toast({ title: 'Erro', description: 'Falha ao publicar.', variant: 'destructive' }); }
  };

  return (
    <div className="aj-wrap py-8">
      <SEO title="Painel do escritório" description="Gerencie seu perfil profissional." />

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#075a6d] uppercase tracking-widest mb-1"><Scale className="w-3.5 h-3.5" /> Painel do escritório</div>
          <h1 className="text-2xl md:text-3xl font-semibold text-[#153e44]">Olá, {firm.leadLawyer}</h1>
          <p className="text-[#425a62] text-sm mt-1">Gerencie o perfil de <span className="font-medium">{firm.name}</span></p>
        </div>
        <Link to={`/escritorio/${firm.slug}`}><Button variant="outline"><Eye className="w-4 h-4 mr-2" /> Ver perfil público</Button></Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {[
          { label: 'Nota média', value: firm.rating ? firm.rating.toFixed(1) : '—', icon: Star },
          { label: 'Avaliações', value: firm.reviews || 0, icon: MessageSquare },
          { label: 'Artigos', value: firm.articles?.length || 0, icon: FileText },
          { label: 'Posts', value: firm.posts?.length || 0, icon: PenTool },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-[#cbd8dc] rounded-[7px] p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-[7px] bg-[#153e44] flex items-center justify-center"><s.icon className="w-5 h-5 text-[#075a6d]" /></div>
            <div><div className="text-xl font-semibold text-[#153e44]">{s.value}</div><div className="text-xs text-[#52676e]">{s.label}</div></div>
          </div>
        ))}
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList className="bg-white border border-[#cbd8dc] p-1 flex-wrap h-auto">
          <TabsTrigger value="perfil">Perfil</TabsTrigger>
          <TabsTrigger value="conteudo">Conteúdo</TabsTrigger>
          <TabsTrigger value="areas">Áreas</TabsTrigger>
          <TabsTrigger value="inbox" data-testid="dashboard-tab-inbox">Inbox</TabsTrigger>
          <TabsTrigger value="crm" data-testid="dashboard-tab-crm">CRM</TabsTrigger>
          <TabsTrigger value="agenda" data-testid="dashboard-tab-agenda">Agenda</TabsTrigger>
          <TabsTrigger value="assinatura" data-testid="dashboard-tab-assinatura">Assinatura</TabsTrigger>
        </TabsList>

        <TabsContent value="perfil" className="mt-4 space-y-4">
          <SeoScoreCard slug={firm.slug} />
          <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-6">
            <h3 className="font-semibold text-[#153e44] mb-4">Informações do escritório</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nome do escritório"><Input value={form.name} onChange={e => set('name', e.target.value)} /></Field>
              <Field label="Advogado(a) responsável"><Input value={form.leadLawyer} onChange={e => set('leadLawyer', e.target.value)} /></Field>
              <Field label="OAB"><Input value={form.oab} onChange={e => set('oab', e.target.value)} /></Field>
              <Field label="Cidade">
                <Select value={form.city} onValueChange={v => set('city', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CITIES.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}/{c.state}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="WhatsApp"><Input value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} /></Field>
              <Field label="Telefone"><Input value={form.phone} onChange={e => set('phone', e.target.value)} /></Field>
              <Field label="E-mail"><Input value={form.email} onChange={e => set('email', e.target.value)} /></Field>
              <Field label="Website"><Input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://..." /></Field>
              <Field label="Endereço" full><Input value={form.address} onChange={e => set('address', e.target.value)} /></Field>
              <Field label="URL da foto/logo" full><Input value={form.photo} onChange={e => set('photo', e.target.value)} /></Field>
              <Field label="URL da capa" full><Input value={form.cover} onChange={e => set('cover', e.target.value)} /></Field>
              <Field label="Bio (breve apresentação)" full><Textarea rows={3} value={form.bio} onChange={e => set('bio', e.target.value)} /></Field>
              <Field label="Sobre o escritório (descrição completa)" full><Textarea rows={6} value={form.description} onChange={e => set('description', e.target.value)} /></Field>
              <Field label="Quem atendemos (um por linha)" full><Textarea rows={3} value={form.whoWeServe} onChange={e => set('whoWeServe', e.target.value)} /></Field>
              <Field label="Diferenciais (um por linha)" full><Textarea rows={3} value={form.differentials} onChange={e => set('differentials', e.target.value)} /></Field>
              <Field label="Prêmios (um por linha)" full><Textarea rows={3} value={form.awards} onChange={e => set('awards', e.target.value)} /></Field>
            </div>
          </div>

          <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-6">
            <h3 className="font-semibold text-[#153e44] mb-4">Redes sociais</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['instagram', 'linkedin', 'facebook', 'youtube'].map(k => (
                <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
                  <Input value={form.social[k] || ''} onChange={e => setSocial(k, e.target.value)} placeholder={`@${k}`} />
                </Field>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-[#153e44] hover:bg-[#0c3036]"><Save className="w-4 h-4 mr-2" /> {saving ? 'Salvando...' : 'Salvar alterações'}</Button>
          </div>
        </TabsContent>

        <TabsContent value="conteudo" className="mt-4 space-y-4">
          <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#153e44]">Artigos publicados ({firm.articles?.length || 0})</h3>
              <Dialog open={articleOpen} onOpenChange={setArticleOpen}>
                <DialogTrigger asChild><Button size="sm" className="bg-[#153e44] hover:bg-[#0c3036]"><Plus className="w-4 h-4 mr-1.5" /> Novo artigo</Button></DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Publicar novo artigo</DialogTitle></DialogHeader>

                  {/* AI Generation panel */}
                  <div className="bg-[#f7fafb] border border-[#cbd8dc] rounded-[7px] p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-[#075a6d]" />
                      <span className="font-semibold text-sm">Gerar rascunho com IA (GPT-5)</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2">
                      <Input value={aiTopic} onChange={e => setAiTopic(e.target.value)} placeholder="Tema (ex: rescisão indireta por atraso de salário)" className="h-10 bg-white border-[#6b858e] text-[#122f36] placeholder:text-[#52676e]" />
                      <Select value={aiArea} onValueChange={setAiArea}>
                        <SelectTrigger className="h-10 bg-white border-[#6b858e] text-[#122f36] w-full md:w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button onClick={generateWithAI} disabled={aiGenerating} className="bg-[#153e44] hover:bg-[#0c3036] text-white h-10">
                        {aiGenerating ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Gerando...</>) : (<><Sparkles className="w-4 h-4 mr-1.5" /> Gerar</>)}
                      </Button>
                    </div>
                    <p className="text-xs text-[#6b858e] mt-2">A IA gera título, resumo e conteúdo completo em markdown. Sempre revise antes de publicar.</p>
                  </div>

                  <div className="space-y-4 mt-2">
                    <Field label="Título"><Input value={article.title} onChange={e => setArticle({ ...article, title: e.target.value })} /></Field>
                    <Field label="Resumo (excerpt)"><Textarea rows={2} value={article.excerpt} onChange={e => setArticle({ ...article, excerpt: e.target.value })} /></Field>
                    <Field label="Conteúdo (Markdown suportado)">
                      <Textarea rows={10} value={article.content} onChange={e => setArticle({ ...article, content: e.target.value })} placeholder="## Introdução&#10;&#10;Escreva ou gere com IA acima..." className="font-mono text-sm" />
                    </Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="URL da imagem de capa (opcional)"><Input value={article.cover} onChange={e => setArticle({ ...article, cover: e.target.value })} /></Field>
                      <Field label="Tempo de leitura"><Input value={article.readTime} onChange={e => setArticle({ ...article, readTime: e.target.value })} /></Field>
                    </div>
                    <Field label="Agendar publicação (opcional)">
                      <div className="relative">
                        <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
                        <Input type="datetime-local" value={article.scheduledAt} onChange={e => setArticle({ ...article, scheduledAt: e.target.value })} className="pl-9" />
                      </div>
                      <p className="text-xs text-[#52676e] mt-1">Deixe em branco para publicar imediatamente. Se agendado, o artigo ficará invisível até a data.</p>
                    </Field>
                    <Button onClick={publishArticle} className="w-full bg-[#153e44] hover:bg-[#0c3036]">{article.scheduledAt ? 'Agendar publicação' : 'Publicar agora'}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="space-y-2">
              {firm.articles?.length === 0 && <p className="text-[#52676e] text-sm italic">Nenhum artigo. Comece publicando conteúdo para atrair clientes!</p>}
              {firm.articles?.map(a => (
                <div key={a.id} className="flex items-center justify-between p-3 bg-[#f7fafb] rounded-[7px]">
                  <div>
                    <div className="font-semibold text-[#153e44] text-sm flex items-center gap-2">
                      {a.title}
                      {a.scheduledAt && new Date(a.scheduledAt) > new Date() && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#153e44] text-white px-2 py-0.5 rounded">Agendado</span>
                      )}
                    </div>
                    <div className="text-xs text-[#52676e]">
                      {a.scheduledAt && new Date(a.scheduledAt) > new Date()
                        ? `Publicação em ${new Date(a.scheduledAt).toLocaleString('pt-BR')}`
                        : `${a.date} • ${a.readTime}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#153e44]">Posts ({firm.posts?.length || 0})</h3>
              <Dialog open={postOpen} onOpenChange={setPostOpen}>
                <DialogTrigger asChild><Button size="sm" className="bg-[#153e44] hover:bg-[#0c3036]"><Plus className="w-4 h-4 mr-1.5" /> Novo post</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Publicar novo post</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <Field label="Mensagem"><Textarea rows={5} value={postContent} onChange={e => setPostContent(e.target.value)} placeholder="O que você gostaria de compartilhar?" /></Field>
                    <Button onClick={publishPost} className="w-full bg-[#153e44] hover:bg-[#0c3036]">Publicar</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="space-y-2">
              {firm.posts?.length === 0 && <p className="text-[#52676e] text-sm italic">Nenhum post publicado.</p>}
              {firm.posts?.map(p => (
                <div key={p.id} className="p-3 bg-[#f7fafb] rounded-[7px]">
                  <p className="text-sm text-[#354c54]">{p.content}</p>
                  <div className="text-xs text-[#52676e] mt-1">{p.date}</div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="areas" className="mt-4">
          <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-6">
            <h3 className="font-semibold text-[#153e44] mb-4">Áreas de atuação</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {CATEGORIES.map(c => {
                const active = form.categories.includes(c.slug);
                return (
                  <button key={c.slug} type="button" onClick={() => toggleCat(c.slug)} className={`text-left px-4 py-3 rounded-[7px] border-2 transition-all ${active ? 'border-[#2f5d65] bg-[#edf4f5] text-[#153e44]' : 'border-[#cbd8dc] hover:border-[#bacbd0] text-[#354c54]'}`}>
                    <span className="font-medium text-sm">{c.name}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end mt-6">
              <Button onClick={save} disabled={saving} className="bg-[#153e44] hover:bg-[#0c3036]"><Save className="w-4 h-4 mr-2" /> Salvar</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="inbox" className="mt-4"><InboxTab /></TabsContent>
        <TabsContent value="crm" className="mt-4"><CrmTab /></TabsContent>
        <TabsContent value="agenda" className="mt-4"><AgendaTab /></TabsContent>
        <TabsContent value="assinatura" className="mt-4"><SubscriptionTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
